/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import {
  AuthenticationService,
  CalculatedPrice,
  CarserviceEstimate,
  Offer,
  PriceRequest,
  ProductExtra,
  Reservation,
  User,
  Vehicle,
} from 'rest-client';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AbsuidService } from '@app/modules/core/services/absuid/absuid.service';
import { AppConfigService } from '@app/modules/core/services/app-config/app-config.service';
import { VehicleService } from '@app/modules/core/services/vehicle/vehicle.service';
import { AppState } from '@app/modules/core/states/app-state/app.state';
import { AuthStateModel } from '@app/modules/core/states/auth-state/auth.model';
import { AuthState } from '@app/modules/core/states/auth-state/auth.state';
import {
  RemoveServiceRequestAction,
  SetOfferAction,
} from '@app/modules/core/states/offer-state/offer.actions';
import { ReservationStateModel } from '@app/modules/core/states/reservation-state/reservation.model';
import { ReservationState } from '@app/modules/core/states/reservation-state/reservation.state';
import { TripStateModel } from '@app/modules/core/states/trip-state/trip.model';
import { TripState } from '@app/modules/core/states/trip-state/trip.state';
import {
  RemoveVehicleAction,
  SetVehicleAction,
} from '@app/modules/core/states/vehicle-state/vehicle.actions';
import { VehicleState } from '@app/modules/core/states/vehicle-state/vehicle.state';
import { getOfferParams } from '@app/modules/core/utils/price-request';
import { GoogleTagManagerService } from '@core/services/analytics/google-tag-manager.service';
import { OfferService } from '@core/services/offer/offer.service';
import { BookingData } from '@core/states/booking-state/booking.model';
import { BookingState } from '@core/states/booking-state/booking.state';
import {
  OfferServices,
  OfferStateModel,
} from '@core/states/offer-state/offer.model';
import { OfferState } from '@core/states/offer-state/offer.state';

import { SummarySections, VehicleData } from '../../models';
import { BookingAction } from '../../models/booking-action';
import { TerminalPlace } from '../../models/gmaps.model';
import { RequestedCarServicesData } from '../../utils/vehicle-status-checker';
import { DateDialogComponent } from '../date-dialog/date-dialog.component';
import { VehicleDialogModifyKmComponent } from '../vehicle-dialog-modify-km/vehicle-dialog-modify-km.component';
import { VehicleDialogComponent } from '../vehicle-dialog/vehicle-dialog.component';
import { VehicleWarningDialogComponent } from '../vehicle-warning-dialog/vehicle-warning-dialog-component';
import { VoucherService } from '@app/modules/core/services/vouchers/vouchers.service';
import { ActivatedRoute } from '@angular/router';

const SERVICE_TYPE_SPACE = '2';
const TYPE_WASH = 'wash';
const PROMO_SUCCESS = 1;
const PROMO_FAILED = 0;

const STATE_SNAPSHOT = 'state_snapshot';
const STATE_TIMESTAMP = 'state_timestamp';

@UntilDestroy()
@Component({
  selector: 'bkg-summary',
  templateUrl: './summary.component.html',
})
export class SummaryComponent implements OnInit, OnChanges {
  @Select(BookingState.bookingData) bookingData$: Observable<BookingData>;
  @Select(OfferState.servicesData)
  servicesData$: Observable<OfferServices>;
  @Select(OfferState.offerData)
  offerData$: Observable<OfferStateModel>;
  @Select(VehicleState.vehicle) vehicleData$: Observable<Vehicle>;
  @Select(TripState.trip) tripData$: Observable<TripStateModel>;
  @Select(AppState.disableButtons) disableButtons$: Observable<number>;
  @Select(AuthState.authData) authData$: Observable<AuthStateModel>;
  @Select(ReservationState.reservationData)
  reservationData$: Observable<ReservationStateModel>;
  @Select(TripState.sourceMode) sourceMode$: Observable<string>;
  @Select(TripState.passengers) passengers$: Observable<string>;
  @Select(TripState.terminals) terminals$: Observable<string>;

  @Input() nextButtonVisible: boolean;
  @Input() bookButtonVisible = false;
  @Input() showSections: SummarySections;
  @Input() imgShow: string;
  @Input() terminalPlace: TerminalPlace;
  @Input() userData: User;
  @Input() hasCarService: boolean;
  @Input() isInvalidBillingForm: boolean;
  @Input() isInvalidReservationForm: boolean;
  @Input() showBilling: boolean;
  @Input() reservation: Reservation;
  @Input() isUrbanParkin: boolean;
  @Input() disableForModify: boolean;
  @Input() isGuest: boolean;
  @Input() billingForm: FormGroup;
  @Input() reservationForm: FormGroup;
  @Input() erpMode: boolean;
  @Input() testABMode: boolean;
  @Input() isModifyData: boolean;
  @Input() isBillingFocus: boolean;
  @Input() showTerms: boolean;

  @Output() clickReturn = new EventEmitter<void>();
  @Output() clickContinue = new EventEmitter<void>();
  @Output() clickBooking = new EventEmitter<{
    acceptPromoTerms: boolean;
    acceptNewsletterTerms: boolean;
  }>();
  @Output() modifyVehicle = new EventEmitter<void>();

  constructor(
    private offerService: OfferService,
    private store: Store,
    private dialog: DialogService,
    private translateService: TranslateService,
    private googleTagManagerService: GoogleTagManagerService,
    private absuidService: AbsuidService,
    private appConfigService: AppConfigService,
    private vehicleService: VehicleService,
    private voucherService: VoucherService,
    private authService: AuthenticationService,
    private route: ActivatedRoute
  ) { }

  offerData: OfferStateModel;
  promoCode: string;
  promoLoaded: number;
  isLoadingPromo = false;
  isLoadingCode = false;
  acceptTerms = false;
  carInsuranceTerms = false;
  carServiceTerms = false;
  acceptPromos = false;
  acceptNewsletter = false;
  disableButtons: number;
  reservationData: Reservation;
  tripData: TripStateModel;
  deletedCarServices: any[] = [];

  serviceData: OfferServices;

  passengers: any;
  terminals: any;

  availableVouchers: any;
  selectedVoucherCode: any;
  private lastCalculatedCode: string | null = null;


  get showAcceptPromos(): boolean {
    return !+this.userData?.accept_promos;
  }

  get showAcceptNewsletter(): boolean {
    return !+this.userData?.accept_newsletter;
  }

  get readOnlyVehicleSection(): boolean {
    return (
      this.showSections.isConfirmation ||
      this.showSections.disableFromUrban ||
      !!this.reservationData?.id
    );
  }

  get showTermsOnModify(): boolean {
    return this.showTerms;
  }

  ngOnInit(): void {
    this.initDataFlow();
    this.subscribeToReservationData();
    this.subscribeToUserData();
    this.subscribeToPassengers();
    this.subscribeToTerminals();
    this.subscribeToDisableButtons();
    this.subscribeToServicesData();
    this.fetchClientVouchers();
    this.subscribeToAvailableVouchers();
    this.subscribeToSelectedVoucherCode()
  }

  ngOnChanges(): void {
    this.initDataFlow();
    this.subscribeToReservationData();
    this.subscribeToUserData();
    this.subscribeToPassengers();
    this.subscribeToTerminals();
    this.subscribeToDisableButtons();
    this.subscribeToServicesData();
    this.subscribeToTripData();
    this.subscribeToAvailableVouchers();
    //this.subscribeToSelectedVoucherCode() //infernal loop
  }

  initDataFlow(): void {
    if (!this.isGuest) {
      this.acceptTerms = true;
    } else if (this.isModifyData) {
      this.acceptTerms = true;
    }
    this.offerData$
      .pipe(untilDestroyed(this))
      .subscribe((offerData: OfferStateModel) => {
        this.offerData = offerData;
        if (
          (this.isBillingFocus) &&
          this.offerData?.offer?.pvp_mechanical_orders > 0
        ) {
          this.carServiceTerms = true;
          this.carInsuranceTerms = true;
        }

      });
  }

  onClickReturn(): void {
    this.clickReturn.emit();
  }

  onContinue(): void {
    localStorage.setItem(STATE_SNAPSHOT, JSON.stringify(this.store.snapshot()));
    localStorage.setItem(STATE_TIMESTAMP, JSON.stringify(new Date().getTime()));
    this.clickContinue.emit();
  }

  onModifyBookingData(): void {
    this.dialog
      .open(DateDialogComponent, {
        closeButton: false,
        size: 'sm',
        backdrop: true,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onSuccessModifyBookingData());
  }

  onModifyVehicle(vehicle: Vehicle): void {
    this.dialog
      .open(VehicleWarningDialogComponent, {
        closeButton: false,
        size: 'sm',
        backdrop: true,
        data: {
          title: this.translateService.instant(
            'sharedModule.bkgVehicleWarningDialog.title'
          ) as string,
          text1: this.translateService.instant(
            'sharedModule.bkgVehicleWarningDialog.textModifyKm'
          ) as string,
        },
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onSuccessModifyVehicleData(vehicle));
  }

  onChangeVehicle(openVehicleDialog = true): void {
    if (!!this.serviceData?.mechanical_orders?.length) {
      this.dialog
        .open(VehicleWarningDialogComponent, {
          closeButton: false,
          size: 'sm',
          backdrop: true,
          data: {
            title: this.translateService.instant(
              'sharedModule.bkgVehicleWarningDialog.title'
            ) as string,
            text1: this.translateService.instant(
              'sharedModule.bkgVehicleWarningDialog.text1'
            ) as string,
            text2: this.translateService.instant(
              'sharedModule.bkgVehicleWarningDialog.text2'
            ) as string,
          },
        })
        .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
        .subscribe(() => this.onSuccessModifyVehicle(openVehicleDialog));

    } else {
      this.onSuccessModifyVehicle(openVehicleDialog)
    }

  }

  onModifyVehicleKm(vehicle: Vehicle): void {
    this.dialog
      .open(VehicleWarningDialogComponent, {
        closeButton: false,
        size: 'sm',
        backdrop: true,
        data: {
          title: this.translateService.instant(
            'sharedModule.bkgVehicleWarningDialog.title'
          ) as string,
          text1: this.translateService.instant(
            'sharedModule.bkgVehicleWarningDialog.textModifyKm'
          ) as string,
        },
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onSuccessModifyVehicleKm(vehicle));
  }

  openVehicleDialog(): void {
    this.dialog.open(VehicleDialogComponent, {
      closeButton: false,
      size: 'sm',
      backdrop: true,
      data: { isModifyVehicle: true },
    });
  }

  openModifyVehicleDialog(vehicle: Vehicle): void {
    vehicle.km = null;
    vehicle.km_timestamp = null;
    const {
      id,
      vehicle_information,
      km,
      km_timestamp: kmTimestamp,
      workshop_type,
    } = vehicle;

    const vehicleData = {} as VehicleData;

    vehicleData.id = id;
    vehicleData.km = km;
    vehicleData.kmTimestamp = kmTimestamp;
    vehicleData.motorCode =
      vehicle_information?.motor_applicabilitycode ?? null;
    vehicleData.bodyCode = vehicle_information?.body_description ?? null;
    vehicleData.bodyDesc = vehicle_information?.body_description ?? null;
    vehicleData.umc = vehicle_information?.umc ?? null;
    vehicleData.umcData = vehicle_information?.umc_data ?? null;
    vehicleData.workshopType = +workshop_type;
    vehicleData.makeCode = vehicle_information?.make_code ?? null;
    this.store.dispatch(new SetVehicleAction(vehicle, vehicleData));
    const requestedCSData: RequestedCarServicesData = {} as RequestedCarServicesData;
    requestedCSData.umc = true;
    requestedCSData.bodyCode = true;
    requestedCSData.motorCode = true;
    requestedCSData.km = true;
    requestedCSData.allNeedsUmc = true;
    this.dialog.open(VehicleDialogComponent, {
      closeButton: false,
      size: 'sm',
      backdrop: true,
      data: {
        requestedCSData: requestedCSData,
        createNewVehicle: false,
        clientId: this.reservationData?.id,
        modifiedVehicle: vehicle,
      },
    });
  }

  onModifyServices(): void {
    //console.log('onModifyServices');
  }

  onRemoveExtra(extra: ProductExtra): void {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'extra-eliminado',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'extra-eliminado',
    });
    const offerParams = this.getRemoveExtraRequest(extra);
    this.calculateOffer(offerParams);
  }

  onRemovePromo(): void {
    this.voucherService.setSelectedVoucherCode(null);
    const offerParams = this.getRemovePromoRequest();
    this.calculateOffer(offerParams);
  }

  onRemovePoints(): void {
    const offerParams = this.getRemovePointsRequest();
    this.calculateOffer(offerParams);
  }

  onRemoveCarService(estimate: CarserviceEstimate): void {
    this.googleTagManagerService.enhancedCommerceEventRemoveFromCart(
      estimate.name,
      estimate.id_car_service,
      estimate.mechanical_estimate_data.base_standard_total
    );



    const offerParams = this.getRemoveCarServiceRequest(estimate);
    this.calculateOffer(offerParams);
  }

  calculateWithRedeem(): void {
    const offerParams = this.getAddRedeemPointsRequest();
    this.isLoadingCode = true;
    this.calculateOffer(offerParams);
  }

  calculateWithPromoCode(promoCode: string): void {
    if (!this.showSections.canShowPrice) return;
    const offerParams = this.getValidatePromoRequest(promoCode);
    this.isLoadingPromo = true;
    this.calculateOfferWithPromo(offerParams);
  }

  cleanPromo(): void {
    this.promoLoaded = null;
    this.promoCode = '';
  }

  onClickBooking(): void {
    let acceptPromoTerms = true;
    let acceptNewsletterTerms = true;
    if (this.showAcceptPromos) {
      acceptPromoTerms = this.acceptPromos;
    }

    if (this.showAcceptNewsletter) {
      acceptNewsletterTerms = this.acceptNewsletter;
    }
    this.clickBooking.emit({ acceptPromoTerms, acceptNewsletterTerms });
  }

  checkForms(): void {
    if (this.isInvalidReservationForm && this.reservationForm)
      this.markFormGroupTouched(this.reservationForm);
    if (this.isInvalidBillingForm && this.billingForm)
      this.markFormGroupTouched(this.billingForm);
  }

  get tooltipMessage(): string {
    let tooltipText = '';
    if (this.invalidTerms) {
      tooltipText += this.translateService.instant(
        'sharedModule.bkgSummary.disabledTerms'
      ) as string;
    }
    if (this.isInvalidReservationForm) {
      const tooltipBooking = this.translateService.instant(
        'sharedModule.bkgSummary.disabledBookingForm'
      ) as string;
      tooltipText += tooltipText ? '\n\n' + tooltipBooking : tooltipBooking;
    }
    if (this.isInvalidBillingForm && this.showBilling) {
      const tooltipBilling = this.translateService.instant(
        'sharedModule.bkgSummary.disabledBillingForm'
      ) as string;
      tooltipText += tooltipText ? '\n\n' + tooltipBilling : tooltipBilling;
    }
    return tooltipText;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    formGroup.markAllAsTouched();
  }

  get disableBooking(): boolean {

    if (!this.showTermsOnModify) {
      return this.invalidForms || this.invalidTerms;
    } else {
      return (
        ((!this.offerData.offer?.mechanical_orders &&
          !this.offerData?.service_request &&
          !this.offerData.customized_tires_request &&
          this.offerData.offer?.extras?.length === 0 &&
          this.disableForModify) ||
          (this.offerData.offer?.mechanical_orders?.length === 0 &&
            !this.offerData?.service_request &&
            !this.offerData.customized_tires_request &&
            this.offerData.offer?.extras?.length === 0 &&
            this.disableForModify)) ||
        this.disableForModify || this.invalidForms || this.invalidTerms
      );
    }
  }

  get showLoadingButton(): boolean {
    return (
      !document.getElementsByClassName('ngneat-dialog-content').length &&
      this.disableButtons > 0
    );
  }

  get disableContinue(): boolean {
    if (!this.isUrbanParkin && !this.disableForModify) {
      return false;
    }
    return (
      (!this.offerData.offer?.mechanical_orders &&
        !this.offerData?.service_request &&
        !this.offerData.customized_tires_request &&
        this.offerData.offer?.extras?.length === 0) ||
      (this.offerData.offer?.mechanical_orders?.length === 0 &&
        !this.offerData?.service_request &&
        !this.offerData.customized_tires_request &&
        this.offerData.offer?.extras?.length === 0) ||
      this.disableForModify
    );
  }

  get invalidForms(): boolean {
    return (
      this.isInvalidReservationForm ||
      (this.isInvalidBillingForm && this.showBilling)
    );
  }

  get invalidTerms(): boolean {
    return (
      !this.acceptTerms ||
      (this.hasCarService && !this.carInsuranceTerms) ||
      (this.hasCarService && !this.carServiceTerms)
    );
  }

  subscribeToDisableButtons(): void {
    this.disableButtons$
      .pipe(untilDestroyed(this))
      .subscribe(
        (disableButtons: number) => (this.disableButtons = disableButtons)
      );
  }

  private subscribeToPassengers(): void {
    this.passengers$.pipe(untilDestroyed(this)).subscribe((passengers) => {
      this.passengers = passengers;
    });
  }

  private subscribeToTerminals(): void {
    this.terminals$.pipe(untilDestroyed(this)).subscribe((terminals) => {
      this.terminals = terminals;
    });
  }

  private subscribeToReservationData(): void {
    this.reservationData$
      .pipe(untilDestroyed(this))
      .subscribe((reservationData: ReservationStateModel) => {
        this.reservationData = reservationData;
        if (
          (this.showTermsOnModify) &&
          this.reservationData?.pvp_mechanical_orders > 0
        ) {
          this.carServiceTerms = true;
          this.carInsuranceTerms = true;
        }
      });
  }

  private subscribeToTripData(): void {
    this.tripData$
      .pipe(untilDestroyed(this))
      .subscribe((tripData: TripStateModel) => {
        this.tripData = tripData;
      });
  }

  private onSuccessModifyBookingData(): void {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'cambio-fechas-reserva',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'cambio-fechas-reserva',
    });
    const offerParams = getOfferParams(
      this.offerData,
      this.absuidService,
      this.tripData
    );
    offerParams.check_carservice_availability = true;
    this.calculateOffer(offerParams);
  }

  private onSuccessModifyVehicle(openVehicleDialog = false): void {
    this.store.dispatch(new RemoveServiceRequestAction());
    if (!!this.serviceData?.mechanical_orders?.length) {
      this.store.dispatch(new RemoveVehicleAction());
      this.removeCarServices();
      if (openVehicleDialog) {
        this.openVehicleDialog();
      }
    } else {
      this.store.dispatch(new RemoveVehicleAction());
      if (openVehicleDialog) {
        this.openVehicleDialog();
      }
    }
  }

  private onSuccessModifyVehicleKm(vehicle: Vehicle): void {
    this.dialog
      .open(VehicleDialogModifyKmComponent, {
        closeButton: false,
        size: 'sm',
        backdrop: true,
        data: { vehicleData: vehicle, isLogged: !!this.userData?.id },
      })
      .afterClosed$.pipe(
        filter((result: Vehicle) => !!result),
        untilDestroyed(this)
      )
      .subscribe((result: Vehicle) => this.updateVehicleKm(result));
  }

  private onSuccessModifyVehicleData(vehicle: Vehicle): void {
    if (!this.isGuest) {
      this.vehicleService
        .clearVehicle(vehicle.id)
        .pipe(untilDestroyed(this))
        .subscribe(
          (returnedData: any) => {
            this.continueModify(returnedData.vehicle as Vehicle);
          },
          (error: HttpErrorResponse) => console.log(error)
        );
    } else {
      this.vehicleService
        .clearVehicleGuest(vehicle.id)
        .pipe(untilDestroyed(this))
        .subscribe(
          (returnedData: any) => {
            this.continueModify(returnedData.vehicle as Vehicle);
          },
          (error: HttpErrorResponse) => console.log(error)
        );
    }
  }

  private continueModify(vehicle: Vehicle): void {
    this.store.dispatch(new RemoveServiceRequestAction());
    if (!!this.serviceData?.mechanical_orders?.length) {
      this.removeCarServices();
      this.openModifyVehicleDialog(vehicle);
    } else {
      this.openModifyVehicleDialog(vehicle);
    }
  }

  private updateVehicleKm(vehicle: Vehicle): void {
    const {
      id,
      vehicle_information,
      km,
      km_timestamp: kmTimestamp,
      workshop_type,
    } = vehicle;
    const vehicleData = {} as VehicleData;
    vehicleData.id = id;
    vehicleData.km = km;
    vehicleData.kmTimestamp = kmTimestamp;
    vehicleData.motorCode =
      vehicle_information?.motor_applicabilitycode ?? null;
    vehicleData.bodyCode = vehicle_information?.body_description ?? null;
    vehicleData.bodyDesc = vehicle_information?.body_description ?? null;
    vehicleData.umc = vehicle_information?.umc ?? null;
    vehicleData.umcData = vehicle_information?.umc_data ?? null;
    vehicleData.workshopType = +workshop_type;
    vehicleData.makeCode = vehicle_information?.make_code ?? null;

    this.store.dispatch([
      new SetVehicleAction(vehicle, vehicleData),
      new RemoveServiceRequestAction(),
    ]);
    this.removeCarServices();
    this.modifyVehicle.emit();
  }

  private removeCarServices(): void {
    const removeOfferData = JSON.parse(
      JSON.stringify(this.offerData)
    ) as OfferStateModel;
    removeOfferData.offer.mechanical_orders = null;
    removeOfferData.offer.pvp_mechanical_orders = 0;
    this.store.dispatch(new SetOfferAction(removeOfferData.offer));
    const offerParams = getOfferParams(removeOfferData, null, this.tripData);
    this.calculateOffer(offerParams);
  }

  private subscribeToServicesData(): void {
    this.servicesData$
      .pipe(untilDestroyed(this))
      .subscribe(
        (offerService: OfferServices) => (this.serviceData = offerService)
      );
  }

  private subscribeToUserData(): void {
    this.authData$
      .pipe(untilDestroyed(this))
      .subscribe((authData: AuthStateModel) => {
        this.userData = authData.user;
        if (this.userData?.id) {
          this.acceptTerms = true;
        }
      });
  }

  private getRemoveExtraRequest(extra: ProductExtra): PriceRequest {
    const offerParams = getOfferParams(this.offerData, null, this.tripData);
    if (extra.type === TYPE_WASH) {
      delete offerParams.id_wash_extra;
    } else if (extra.service_type === SERVICE_TYPE_SPACE) {
      delete offerParams.id_space_extra;
    }
    return offerParams;
  }

  private getRemovePromoRequest(): PriceRequest {
    const offerParams = getOfferParams(this.offerData, null, this.tripData);
    offerParams.promoCode = null;
    return offerParams;
  }

  private getRemovePointsRequest(): PriceRequest {
    const offerParams = getOfferParams(this.offerData, null, this.tripData);
    offerParams.use_points = false;
    return offerParams;
  }

  private getRemoveCarServiceRequest(
    estimate: CarserviceEstimate
  ): PriceRequest {

    this.deletedCarServices.push(estimate.id_car_service);
    const deleted_id_carservice = this.deletedCarServices.join(',');
    const offerParams = {
      ...getOfferParams(this.offerData, null, this.tripData),
      delete_id_car_service: deleted_id_carservice,
    };
    if (this.deletedCarServices.length && this?.reservationData?.id) {
      offerParams.id_mechanical_estimate = null;
    }
    return offerParams;
  }

  private getAddRedeemPointsRequest(): PriceRequest {
    const offerParams = {
      ...getOfferParams(this.offerData, null, this.tripData),
      use_points: true,
    };
    return offerParams;
  }

  private getValidatePromoRequest(promoCode: string): PriceRequest {
    const offerParams = {
      ...getOfferParams(this.offerData, null, this.tripData),
      promoCode: promoCode,
    };
    return offerParams;
  }

  private calculateOffer(offerParams: PriceRequest): void {
    if (this.reservationData?.id) {
      this.updateReservationOffer(offerParams);
      return;
    }
    this.offerService
      .getOffer(offerParams)
      .pipe(untilDestroyed(this))
      .subscribe((offer: Offer) => {
        this.store.dispatch(new SetOfferAction(offer));
        this.isLoadingCode = false;
      });
  }

  private calculateOfferWithPromo(offerParams: PriceRequest): void {
    offerParams = this.getOtherCalculateParams(offerParams);

    if (this.reservationData?.id) {
      this.updateReservationOfferWithPromo(offerParams);
      return;
    }
    this.offerService
      .getOffer(offerParams)
      .pipe(untilDestroyed(this))
      .subscribe((offer: Offer) => {
        this.promoCode = '';
        this.promoLoaded =
          !!offer?.promos.length && !offer.promos?.[0]?.error
            ? PROMO_SUCCESS
            : PROMO_FAILED;
        this.isLoadingPromo = false;
        this.store.dispatch(new SetOfferAction(offer));
      });
  }

  private updateReservationOffer(offerParams: PriceRequest): void {
    offerParams = this.getOtherCalculateParams(offerParams);

    this.offerService
      .getOfferFromReservation(
        this.reservationData.id.toString(),
        this.reservationData.hash,
        offerParams
      )
      .pipe(untilDestroyed(this))
      .subscribe((fromReservation: CalculatedPrice) => {
        const { offer } = fromReservation;
        this.store.dispatch(new SetOfferAction(offer));
        this.isLoadingCode = false;
      });
  }

  private updateReservationOfferWithPromo(offerParams: PriceRequest): void {
    offerParams = this.getOtherCalculateParams(offerParams);

    this.offerService
      .getOfferFromReservation(
        this.reservationData.id.toString(),
        this.reservationData.hash,
        offerParams
      )
      .pipe(untilDestroyed(this))
      .subscribe((fromReservation: CalculatedPrice) => {
        const { offer } = fromReservation;
        this.promoCode = '';
        this.promoLoaded =
          !!offer?.promos.length && !offer.promos?.[0]?.error
            ? PROMO_SUCCESS
            : PROMO_FAILED;
        this.isLoadingPromo = false;
        this.store.dispatch(new SetOfferAction(offer));
      });
  }

  private getOtherCalculateParams(offerParams: PriceRequest): PriceRequest {
    this.fetchClientVouchers();
    offerParams.absuid = this.absuidService.getAbsuid();
    offerParams.form_version = this.appConfigService.getFormVersionStorage();
    offerParams.step = this.absuidService.getTestParams().step;
    offerParams.experiment = this.absuidService.getTestParams().experiment;
    offerParams.screen_resolution = this.absuidService.getTestParams().screenResolution;

    offerParams.in_terminal = this.terminals?.in_terminal || null;
    offerParams.out_terminal = this.terminals?.out_terminal || null;
    offerParams.in_passengers = this.passengers?.in_passengers || null;
    offerParams.out_passengers = this.passengers?.out_passengers || null;

    return offerParams;
  }

  subscribeToSelectedVoucherCode(): void {
    this.voucherService.selectedVoucherCode$
      .pipe(untilDestroyed(this))
      .subscribe((code) => {
        this.selectedVoucherCode = code;

        const shouldRecalculate =
          this.selectedVoucherCode &&
          this.showSections.canShowPrice &&
          this.selectedVoucherCode !== this.lastCalculatedCode;

        if (shouldRecalculate || !this.selectedVoucherCode) {
          this.lastCalculatedCode = this.selectedVoucherCode;
          this.calculateWithPromoCode(this.selectedVoucherCode);
        }
      });
  }

  subscribeToAvailableVouchers(): void {
    this.bookingData$
      .pipe(untilDestroyed(this))
      .subscribe((bookingData) => {
        if (!bookingData?.startDate || !bookingData?.endDate) {
          console.warn('Fechas no disponibles en bookingData');
          return;
        }

        const data = {
          start_date: bookingData.startDate,
          end_date: bookingData.endDate,
          id_lot: bookingData.product?.idLot || 0,
          id_product: bookingData.product?.id || 0,
        };

        this.availableVouchers = this.voucherService.listAvailableVouchers(data);
      });
  }

  get showAvailableVouchers() {
    const hasVouchers = this.availableVouchers?.length > 0;
    const hasModifyResToken = this.route.snapshot.queryParamMap.has('modify_res_token');

    return hasVouchers && !hasModifyResToken;
  }


  fetchClientVouchers() {
    if (this.authService.isTokenValid() && this.authService.isClient()) {
      {
        this.voucherService
          .fetchClientVouchers()
          .pipe(untilDestroyed(this))
          .subscribe();
      }
    }
  }

}
